import type { LucideIcon } from 'lucide-react';
import {
  Building2,
  Compass,
  Factory,
  GitBranch,
  Home,
  Layers,
  LayoutGrid,
  Search,
  Sparkles,
  Users,
  Waypoints,
} from 'lucide-react';
import { NodeThumb } from '@/components/value-chain/explorer/NodeThumb';
import { shortName } from '@/components/value-chain/nodeVisuals';
import type { ValueChainNavId, ValueChainNode, ValueChainRecentItem } from '@/types/valueChain';

const NAV: Array<{ id: ValueChainNavId; label: string; icon: LucideIcon }> = [
  { id: 'home', label: 'Home', icon: Home },
  { id: 'explore', label: 'Explore', icon: Compass },
  { id: 'revenue-map', label: 'Revenue Map', icon: LayoutGrid },
  { id: 'chains', label: 'Chains', icon: Waypoints },
  { id: 'opportunities', label: 'Opportunities', icon: Sparkles },
  { id: 'companies', label: 'Companies', icon: Building2 },
  { id: 'materials', label: 'Materials', icon: Layers },
  { id: 'manufacturing', label: 'Manufacturing', icon: Factory },
  { id: 'customers', label: 'Customers', icon: Users },
  { id: 'graph', label: 'Graph (Full)', icon: GitBranch },
];

type Props = {
  active: ValueChainNavId;
  onNavigate: (id: ValueChainNavId) => void;
  recent: ValueChainRecentItem[];
  recentNodes: ValueChainNode[];
  onSelectRecent: (id: string) => void;
  searchQuery: string;
  onSearchQueryChange: (value: string) => void;
  onSubmitSearch: () => void;
};

export function ExplorerSidebar({
  active,
  onNavigate,
  recent,
  recentNodes,
  onSelectRecent,
  searchQuery,
  onSearchQueryChange,
  onSubmitSearch,
}: Props) {
  return (
    <aside className="flex h-full w-[232px] shrink-0 flex-col bg-slate-950 text-slate-200">
      <div className="flex items-center gap-2.5 px-4 py-5">
        <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-blue-500 text-white">
          <GitBranch className="h-4 w-4" />
        </span>
        <span>
          <span className="block text-sm font-semibold text-white">Value Chain</span>
          <span className="block text-[11px] text-slate-400">Explorer</span>
        </span>
      </div>

      <nav className="px-2">
        {NAV.map((item) => {
          const Icon = item.icon;
          const selected = active === item.id;
          return (
            <button
              key={item.id}
              type="button"
              onClick={() => onNavigate(item.id)}
              className={`mb-0.5 flex w-full items-center gap-2.5 rounded-lg px-3 py-2 text-sm transition-colors ${
                selected
                  ? 'bg-slate-800 font-medium text-white'
                  : 'text-slate-400 hover:bg-slate-900 hover:text-white'
              }`}
            >
              <Icon className="h-4 w-4" />
              {item.label}
            </button>
          );
        })}
      </nav>

      <div className="mt-6 px-4">
        <p className="mb-2 text-[10px] font-semibold tracking-[0.14em] text-slate-500 uppercase">
          Recently viewed
        </p>
        <div className="space-y-1">
          {recent.length === 0 ? (
            <p className="text-[12px] text-slate-500">Select a node to start a trail.</p>
          ) : (
            recent.map((item) => {
              const node = recentNodes.find((entry) => entry.id === item.id);
              return (
                <button
                  key={`${item.graphId}-${item.id}`}
                  type="button"
                  onClick={() => onSelectRecent(item.id)}
                  className="flex w-full items-center gap-2 rounded-lg px-1.5 py-1.5 text-left hover:bg-slate-900"
                >
                  {node ? (
                    <NodeThumb node={node} size="sm" />
                  ) : (
                    <span className="h-9 w-9 rounded-xl bg-slate-800" />
                  )}
                  <span className="min-w-0">
                    <span className="block truncate text-[13px] text-slate-100">
                      {shortName(item.name)}
                    </span>
                    <span className="block truncate text-[11px] text-slate-500">{item.type}</span>
                  </span>
                </button>
              );
            })
          )}
        </div>
      </div>

      <div className="mt-auto border-t border-slate-800 p-3">
        <label className="relative block">
          <Search className="absolute top-1/2 left-3 h-3.5 w-3.5 -translate-y-1/2 text-slate-500" />
          <input
            value={searchQuery}
            onChange={(event) => onSearchQueryChange(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter') onSubmitSearch();
            }}
            placeholder="Search anything..."
            className="w-full rounded-lg border border-slate-800 bg-slate-900 py-2 pr-3 pl-9 text-[12px] text-slate-100 outline-none placeholder:text-slate-500"
          />
        </label>
      </div>
    </aside>
  );
}
